<!--
AssistantDiagnosticsChip — discreet one-line diagnostics pill for M6 (plan D6).

Rendered ONLY by AssistantThread when:
  - permissionsService.isUserInGroups(['Full'])
  - message.role === 'assistant'
  - message.diagnostics !== null

Clicking the chip toggles an inline popover with the full PII-free grid:
  - tokens (prompt / resposta / total)
  - finishReason, iterations, requestId
  - per-tool rows: name / outcome / errorCode? / latencyMs

SECURITY INVARIANT (mirror of AssistantMessageDiagnostics.vue): renders ONLY
the PII-free fields surfaced by parseDiagnostics(). Never renders raw tool args
or backend response bodies.
-->
<template>
  <div class="adc-wrapper">
    <!-- One-line muted pill -->
    <button
      type="button"
      class="adc-chip"
      data-testid="assistant-diagnostics-chip"
      :aria-expanded="open"
      :aria-label="t('Diagnóstico da resposta')"
      @click="toggle"
    >
      <span class="adc-icon" aria-hidden="true">ⓘ</span>
      <span>{{ summary }}</span>
    </button>

    <!-- Inline popover — toggles below the chip -->
    <div
      v-if="open"
      class="adc-popover"
      role="dialog"
      data-testid="assistant-diagnostics-popover"
      :aria-label="t('Detalhes do diagnóstico')"
    >
      <dl class="adc-grid">
        <div class="adc-row">
          <dt>{{ t('Tokens (prompt / resposta / total)') }}</dt>
          <dd>{{ fmt(diagnostics.promptTokens) }} / {{ fmt(diagnostics.completionTokens) }} / {{ fmt(diagnostics.totalTokens) }}</dd>
        </div>
        <div class="adc-row">
          <dt>{{ t('Motivo de término') }}</dt>
          <dd>{{ diagnostics.finishReason }}</dd>
        </div>
        <div class="adc-row">
          <dt>{{ t('Iterações') }}</dt>
          <dd>{{ diagnostics.iterations }}</dd>
        </div>
        <div class="adc-row">
          <dt>{{ t('ID da requisição') }}</dt>
          <dd class="adc-request-id">{{ diagnostics.requestId }}</dd>
        </div>
        <!-- Raw micro-credit accounting row — present only when billing ran (A1 sanctioned raw-micro surface). -->
        <div
          v-if="hasMicros"
          class="adc-row"
          data-testid="adc-micros-row"
        >
          <dt>{{ t('Créditos (usados · antes → depois)') }}</dt>
          <dd class="adc-micros">
            <!-- Separate testid elements expose individual raw-micro values for
                 the A6 smoke (assistant-diagnostics-credits-used / -balance).
                 The combined microsLabel remains for human readability. -->
            <span
              data-testid="assistant-diagnostics-credits-used"
              class="adc-micros-field"
            >{{ diagnostics.creditsUsedMicro ?? 0 }}</span>
            <span class="adc-micros-sep"> · </span>
            <span
              data-testid="assistant-diagnostics-balance"
              class="adc-micros-field"
            >{{ diagnostics.balanceAfterMicro ?? 0 }}</span>
            <span class="adc-micros-hint"> ({{ microsLabel }})</span>
          </dd>
        </div>
      </dl>

      <template v-if="diagnostics.tools.length > 0">
        <div class="adc-divider" />
        <ul class="adc-tool-list">
          <li
            v-for="(tool, idx) in diagnostics.tools"
            :key="idx"
            class="adc-tool-row"
            :data-testid="`adc-tool-${idx}`"
          >
            <span class="adc-tool-name">{{ tool.name }}</span>
            <span
              class="adc-tool-outcome"
              :class="tool.outcome === 'ok' ? 'adc-tool-ok' : 'adc-tool-error'"
            >{{ tool.outcome }}</span>
            <span v-if="tool.errorCode" class="adc-tool-code">{{ tool.errorCode }}</span>
            <span class="adc-tool-latency">{{ tool.latencyMs }} ms</span>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { AgentDiagnostics } from '../../composables/aiAgentContract';
import { formatCredits } from '../assistant/formatCredits';

const props = defineProps<{
  diagnostics: AgentDiagnostics;
}>();

const { t } = useI18n();

const open = ref<boolean>(false);

function toggle(): void {
  open.value = !open.value;
}

function fmt(value: number | null): string {
  return value === null ? '—' : String(value);
}

/** One-liner summary shown in the chip. */
const summary = computed<string>(() => {
  const tokens = fmt(props.diagnostics.totalTokens);
  const tools = props.diagnostics.tools.length;
  // Latency: sum of per-tool latencies as a proxy (overall turn latency is not
  // surfaced by the agent; summing tool latencies gives a useful signal).
  const latencyTotal = props.diagnostics.tools.reduce((acc, t) => acc + t.latencyMs, 0);
  const latencyStr = latencyTotal > 0 ? ` · ${latencyTotal} ms` : '';
  return `${tokens} tokens · ${tools} ${t('ferramentas')}${latencyStr}`;
});

/**
 * True when at least one micro-credit accounting field is present (non-undefined).
 * A null value means billing ran but the field was explicitly null — still show the row.
 */
const hasMicros = computed<boolean>(() => {
  const d = props.diagnostics;
  return (
    d.creditsUsedMicro !== undefined ||
    d.balanceBeforeMicro !== undefined ||
    d.balanceAfterMicro !== undefined
  );
});

/** Formats a raw micro value: the integer verbatim + optional humanized hint. */
function fmtMicro(value: number | null | undefined): string {
  if (value === undefined || value === null) return '—';
  const hint = formatCredits(value);
  return `${value} µcr (${hint} cr)`;
}

/**
 * Raw-micro row label: "used · before → after" verbatim integers with a
 * humanized formatCredits() hint in parentheses. This is the ONE sanctioned
 * raw-micro surface in the entire app (A1 plan).
 */
const microsLabel = computed<string>(() => {
  const used = fmtMicro(props.diagnostics.creditsUsedMicro);
  const before = fmtMicro(props.diagnostics.balanceBeforeMicro);
  const after = fmtMicro(props.diagnostics.balanceAfterMicro);
  return `${used} · ${before} → ${after}`;
});
</script>

<style scoped>
.adc-wrapper {
  position: relative;
  display: inline-block;
}

/* ── Chip ─────────────────────────────────────────────────────────────────── */

.adc-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  padding: 0.15rem 0.35rem;
  font-size: 0.7rem;
  color: var(--p-text-muted-color, #64748b);
  cursor: pointer;
  border-radius: 9999px;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  user-select: none;
}

.adc-chip:hover {
  background: var(--p-surface-100, #f1f5f9);
  color: var(--p-text-color, #1e293b);
}

.adc-chip[aria-expanded='true'] {
  background: var(--p-surface-100, #f1f5f9);
  color: var(--p-text-color, #1e293b);
}

.adc-icon {
  font-style: normal;
  font-size: 0.75rem;
  opacity: 0.7;
}

/* ── Popover ──────────────────────────────────────────────────────────────── */

.adc-popover {
  position: absolute;
  bottom: calc(100% + 0.35rem);
  left: 0;
  z-index: 50;
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e2e8f0);
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 0.75rem;
  min-width: 18rem;
  max-width: 24rem;
}

/* ── Diagnostics grid ─────────────────────────────────────────────────────── */

.adc-grid {
  margin: 0;
  display: grid;
  gap: 0.3rem;
}

.adc-row {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  font-size: 0.78rem;
}

.adc-row dt {
  font-weight: 600;
  color: var(--p-text-muted-color, #64748b);
  margin: 0;
}

.adc-row dd {
  margin: 0;
  word-break: break-all;
  color: var(--p-text-color, #1e293b);
}

.adc-request-id {
  font-family: ui-monospace, monospace;
  font-size: 0.72rem;
  color: var(--p-text-muted-color, #64748b);
}

.adc-divider {
  height: 1px;
  background: var(--p-surface-200, #e2e8f0);
  margin: 0.5rem 0;
}

/* ── Tool list ────────────────────────────────────────────────────────────── */

.adc-tool-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.adc-tool-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
}

.adc-tool-name {
  font-family: ui-monospace, monospace;
  flex: 1;
  color: var(--p-text-color, #1e293b);
  word-break: break-word;
}

.adc-tool-outcome {
  text-transform: uppercase;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.adc-tool-ok {
  color: var(--app-success-color, #16a34a);
}

.adc-tool-error {
  color: var(--app-danger-color, #dc2626);
}

.adc-tool-code {
  font-family: ui-monospace, monospace;
  font-size: 0.7rem;
  color: var(--app-danger-color, #dc2626);
  flex-shrink: 0;
}

.adc-tool-latency {
  margin-left: auto;
  color: var(--p-text-muted-color, #64748b);
  flex-shrink: 0;
}

.adc-micros {
  font-family: ui-monospace, monospace;
  font-size: 0.72rem;
  word-break: break-all;
}

.adc-micros-field {
  font-weight: 600;
}

.adc-micros-sep {
  opacity: 0.5;
}

.adc-micros-hint {
  opacity: 0.6;
  font-size: 0.68rem;
}
</style>
